/**
 * DetailPanel Component
 *
 * Universal right-side panel for all entity details, forms, and aggregations.
 *
 * - Desktop: Opens on the right side of the screen
 * - Mobile: Full-screen modal with close button
 *
 * This is the standard pattern for viewing/editing ANY entity in the platform.
 * Everything is clickable → opens in a DetailPanel.
 */

import React, { useEffect, useRef, type ReactNode, type CSSProperties } from 'react';
import { X, ChevronRight } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface DetailPanelProps {
  /** Whether the panel is visible (default: true when rendered) */
  isOpen?: boolean;
  /** Close handler */
  onClose: () => void;
  /** Panel title */
  title: string;
  /** Optional subtitle (e.g., entity type, status) */
  subtitle?: string;
  /** Icon to show next to title */
  icon?: ReactNode;
  /** Accent color for left border (preferred over headerColor) */
  accentColor?: string;
  /** @deprecated Use accentColor instead */
  headerColor?: string;
  /** Main content */
  children: ReactNode;
  /** Footer content (e.g., action buttons) */
  footer?: ReactNode;
  /** Width on desktop (default: 50%) */
  width?: number | string;
  /** Additional class name */
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function DetailPanel({
  isOpen = true,
  onClose,
  title,
  subtitle,
  icon,
  accentColor,
  headerColor,
  children,
  footer,
  width = '50%',
  className = '',
}: DetailPanelProps) {
  // Use accentColor if provided, otherwise fall back to headerColor
  const borderColor = accentColor || headerColor;
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop (mobile only, click to close) */}
      <div
        className="detail-panel-backdrop"
        style={styles.backdrop}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`detail-panel ${className}`}
        style={{
          ...styles.panel,
          width: typeof width === 'number' ? `${width}px` : width,
        }}
      >
        {/* Header */}
        <div
          style={{
            ...styles.header,
            ...(borderColor ? { borderLeftColor: borderColor } : {}),
          }}
        >
          <div style={styles.headerContent}>
            {icon && <div style={styles.headerIcon}>{icon}</div>}
            <div style={styles.headerText}>
              <h2 style={styles.title}>{title}</h2>
              {subtitle && <div style={styles.subtitle}>{subtitle}</div>}
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div style={styles.footer}>
            {footer}
          </div>
        )}
      </div>

      {/* Styles injected for responsive behavior */}
      <style>{responsiveStyles}</style>
    </>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Section within DetailPanel
 */
export function DetailSection({
  title,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  title?: string;
  children: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div style={styles.section}>
      {title && (
        <div
          style={{
            ...styles.sectionHeader,
            cursor: collapsible ? 'pointer' : 'default',
          }}
          onClick={() => collapsible && setIsOpen(!isOpen)}
        >
          <span style={styles.sectionTitle}>{title}</span>
          {collapsible && (
            <ChevronRight
              size={16}
              style={{
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0)',
                transition: 'transform 0.2s',
                color: '#9ca3af',
              }}
            />
          )}
        </div>
      )}
      {(!collapsible || isOpen) && (
        <div style={styles.sectionContent}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Form field within DetailPanel
 */
export function DetailField({
  label,
  children,
  required = false,
  error,
}: {
  label: string;
  children: ReactNode;
  required?: boolean;
  error?: string;
}) {
  return (
    <div style={styles.field}>
      <label style={styles.fieldLabel}>
        {label}
        {required && <span style={styles.required}>*</span>}
      </label>
      {children}
      {error && <div style={styles.fieldError}>{error}</div>}
    </div>
  );
}

/**
 * Cross-reference item showing aggregation count
 * Clicking navigates to filtered list
 */
export function CrossReferenceItem({
  icon,
  label,
  count,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  count: number;
  onClick?: () => void;
}) {
  const [hovered, setHovered] = React.useState(false);

  if (count === 0) return null;

  return (
    <div
      style={{
        ...styles.crossRefItem,
        background: hovered ? 'var(--bg-hover, #f5f7fa)' : 'transparent',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.crossRefIcon}>{icon}</div>
      <span style={styles.crossRefLabel}>{label}</span>
      <span style={styles.crossRefCount}>{count}</span>
      <ChevronRight size={14} style={{ color: '#9ca3af' }} />
    </div>
  );
}

/**
 * Cross-references section
 */
export function CrossReferences({
  title = 'Usato in',
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <DetailSection title={title}>
      <div style={styles.crossRefList}>
        {children}
      </div>
    </DetailSection>
  );
}

/**
 * Action buttons row for footer
 */
export function DetailActions({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div style={styles.actions}>
      {children}
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.3)',
    zIndex: 999,
    // Always visible - click to close on both desktop and mobile
  },
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100vh',
    background: 'var(--bg-panel, #ffffff)',
    borderLeft: '1px solid var(--line, #e5e7eb)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.08)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--line, #e5e7eb)',
    borderLeft: '4px solid var(--brand, #8b5cf6)',
    background: 'var(--bg, #fafafa)',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  headerIcon: {
    flexShrink: 0,
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'var(--bg-icon, #f3f4f6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--text, #1f2937)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: '12px',
    color: 'var(--text-muted, #6b7280)',
    marginTop: '2px',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    color: 'var(--text-muted, #6b7280)',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid var(--line, #e5e7eb)',
    background: 'var(--bg, #fafafa)',
  },
  section: {
    marginBottom: '20px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--text-muted, #6b7280)',
  },
  sectionContent: {},
  field: {
    marginBottom: '16px',
  },
  fieldLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text, #374151)',
    marginBottom: '6px',
  },
  required: {
    color: '#ef4444',
    marginLeft: '2px',
  },
  fieldError: {
    fontSize: '12px',
    color: '#ef4444',
    marginTop: '4px',
  },
  crossRefList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    margin: '0 -12px',
  },
  crossRefItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'background 0.15s',
  },
  crossRefIcon: {
    flexShrink: 0,
    color: 'var(--text-muted, #6b7280)',
  },
  crossRefLabel: {
    flex: 1,
    fontSize: '13px',
    color: 'var(--text, #374151)',
  },
  crossRefCount: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text, #374151)',
    background: 'var(--bg, #f3f4f6)',
    padding: '2px 8px',
    borderRadius: '10px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
};

// =============================================================================
// RESPONSIVE CSS
// =============================================================================

const responsiveStyles = `
  /* Mobile: Full screen modal */
  @media (max-width: 767px) {
    .detail-panel-backdrop {
      display: block !important;
    }
    .detail-panel {
      width: 100% !important;
      max-width: 100% !important;
      border-left: none !important;
      border-radius: 0 !important;
    }
  }

  /* Desktop: Side panel - backdrop always visible for click-to-close */
  @media (min-width: 768px) {
    /* Backdrop visible on desktop too */
  }

  /* Hover states */
  .detail-panel button:hover {
    background: var(--bg-hover, #f3f4f6) !important;
  }
`;
